import {findRepSupportTopic} from './rep-knowledge';
import {getRepConcepts} from './rep-vocabulary';

const SPORT_ALIASES = {
  tennis: ['tennis'],
  padel: ['padel'],
  pickleball: ['pickleball', 'pickelball'],
  golf: ['golf'],
  calcio: ['calcio', 'football', 'soccer', 'fussball'],
  basket: ['basket', 'basketball'],
  pallavolo: ['pallavolo', 'volley', 'volleyball'],
  rugby: ['rugby'],
  nuoto: ['nuoto', 'swimming', 'natation', 'schwimmen']
};
const MY_VIDEO_SECTIONS = [
  {key: 'partner_videos', aliases: ['partner video']},
  {key: 'video', aliases: ['replayer video']},
  {key: 'highlight', aliases: ['highlight', 'clip', 'taglio', 'ritaglio', 'spezzone', 'estratto']},
  {key: 'analysis', aliases: ['analisi', 'pb vision', 'pbvision', 'statistiche partita']},
  {key: 'playlist', aliases: ['playlist']},
  {key: 'tags', aliases: ['tag']}
];
const CONTENT_SEARCH_STOP_WORDS = new Set([
  'apri', 'aprire', 'cerca', 'cercami', 'fammi', 'mostra', 'mostrami', 'trova', 'trovami',
  'vedere', 'guarda', 'video', 'partita', 'partite', 'replayertv', 'replayer', 'della', 'delle',
  'degli', 'dell', 'del', 'nella', 'nelle', 'sulla', 'sulle', 'una', 'ultimo', 'ultima',
  'recente', 'contro', 'versus', 'against', 'tv', 'oggi', 'ieri', 'domani', 'dopodomani',
  'dove', 'open', 'find', 'show', 'watch', 'match', 'matches', 'game', 'games', 'the', 'of',
  'last', 'recent', 'today', 'yesterday', 'tomorrow', 'where', 'ouvre', 'cherche', 'trouve',
  'montre', 'regarder', 'partie', 'parties', 'derniere', 'aujourd', 'hier', 'demain', 'ou',
  'offne', 'suche', 'finde', 'zeige', 'ansehen', 'spiel', 'spiele', 'letzte', 'heute',
  'gestern', 'morgen', 'wo'
]);
const PARTNER_SEARCH_STOP_WORDS = new Set([
  'apri', 'aprire', 'cerca', 'cercare', 'campo', 'campi', 'libero', 'liberi',
  'disponibile', 'disponibili', 'prenota', 'prenotare', 'crea', 'creare',
  'organizza', 'organizzare', 'partita', 'partite', 'aperta', 'aperte', 'gioca',
  'giocare', 'domani', 'dopodomani', 'oggi', 'alle', 'delle', 'della', 'alla',
  'nella', 'vorrei', 'replayer', 'replayertv', 'una', 'per', 'del', 'dei', 'con',
  'singolo', 'doppio', 'amichevole', 'competitiva', 'maschile', 'femminile', 'mista',
  'open', 'find', 'search', 'book', 'create', 'organize', 'organise', 'match',
  'matches', 'game', 'play', 'tomorrow', 'today', 'available', 'availability',
  'court', 'field', 'for', 'with', 'friendly', 'competitive', 'singles', 'doubles',
  'ouvre', 'cherche', 'trouve', 'reserve', 'creer', 'cree', 'organise', 'partie',
  'parties', 'ouverte', 'ouvertes', 'demain', 'aujourd', 'terrain', 'disponible',
  'offne', 'suche', 'finde', 'buche', 'erstelle', 'organisiere', 'offenes',
  'spiel', 'spiele', 'morgen', 'heute', 'platz', 'verfugbar'
]);

const normalize = value => (value || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9€.,:-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizePartnerSport = value => normalize(value)
  .replace(/^sport\s*/, '')
  .replace(/^replayer\s*/, '')
  .trim();

const canonicalPartnerSport = value => {
  const normalized = normalizePartnerSport(value);
  return Object.keys(SPORT_ALIASES).find(sport => sport === normalized
    || SPORT_ALIASES[sport].some(alias => normalized.includes(alias))) || null;
};

const partnerBookableSports = partner => {
  const listedSports = Array.isArray(partner && partner.bookableSports) ? partner.bookableSports : [];
  const fieldSports = Array.isArray(partner && partner.fields)
    ? partner.fields.filter(field => field && field.bookable === true).map(field => field.sport)
    : [];
  return Array.from(new Set([...listedSports, ...fieldSports]
    .map(canonicalPartnerSport)
    .filter(Boolean)));
};

export const getRepBookableSports = (partners, intent = 'booking') => Array.from(new Set(
  (partners || [])
    .filter(partner => intent !== 'create-open-match' || partner.openMatchesEnabled === true)
    .reduce((sports, partner) => sports.concat(partnerBookableSports(partner)), [])
)).sort();

const repPartnerAction = (id, labelKey, options) => ({id, labelKey, ...options});
const repPartnerWorkspace = (mode, actions) => ({
  mode,
  actions,
  introKey: mode === 'general' ? 'REP_INTRO_PARTNER' : `REP_INTRO_PARTNER_${mode.toUpperCase()}`,
  placeholderKey: mode === 'general' ? 'REP_PLACEHOLDER_PARTNER' : `REP_PLACEHOLDER_PARTNER_${mode.toUpperCase()}`
});

export const getRepPartnerWorkspace = ({
  profile = null,
  fields = [],
  services = [],
  channels = [],
  videos = [],
  partnerVideos = [],
  playlists = []
} = {}) => {
  const safeFields = Array.isArray(fields) ? fields : [];
  const safeServices = Array.isArray(services) ? services : [];
  const profileChannels = profile && Array.isArray(profile.rtvChannels) ? profile.rtvChannels : [];
  const safeChannels = Array.isArray(channels) && channels.length ? channels : profileChannels;
  const hasBookableFields = safeFields.some(field => field && field.bookable === true);
  const hasBookableServices = safeServices.some(service => service && service.bookable === true);
  const hasBooking = hasBookableFields || hasBookableServices;
  const hasRecordingResources = [...safeFields, ...safeServices]
    .some(resource => resource && resource.recording === true);
  const hasMedia = hasRecordingResources
    || safeChannels.length > 0
    || (Array.isArray(videos) && videos.length > 0)
    || (Array.isArray(partnerVideos) && partnerVideos.length > 0)
    || (Array.isArray(playlists) && playlists.length > 0);
  const openMatchesEnabled = Boolean(profile && profile.openMatchesEnabled);
  const canCreateOpenMatch = openMatchesEnabled && hasBookableFields;
  const bookingRoute = hasBookableFields ? '/partner-booking-calendar' : '/partner-service-booking-calendar';
  const resources = repPartnerAction('resources', 'REP_PARTNER_ACTION_RESOURCES', {route: '/partner-fields', icon: 'calendar'});
  const bookings = repPartnerAction('bookings', 'REP_PARTNER_ACTION_BOOKINGS', {route: bookingRoute, icon: 'calendar'});
  const openMatch = repPartnerAction('open-match', 'REP_PARTNER_ACTION_OPEN_MATCH', {route: '/partner-open-matches/create', icon: 'users'});
  const planActivity = repPartnerAction('plan-activity', 'REP_PARTNER_ACTION_PLAN_ACTIVITY', {route: '/partner-fields', icon: 'calendar'});
  const reviewVideos = repPartnerAction('review-videos', 'REP_PARTNER_ACTION_REVIEW_VIDEOS', {section: 'video', icon: 'video'});
  const highlights = repPartnerAction('highlights', 'REP_PARTNER_ACTION_HIGHLIGHTS', {section: 'highlight', icon: 'video'});
  const managePlaylists = repPartnerAction('playlists', 'REP_PARTNER_ACTION_PLAYLISTS', {section: 'playlist', icon: 'playlist'});
  const replayerTv = repPartnerAction('replayer-tv', 'REP_PARTNER_ACTION_RTV', {route: '/partner-replayer-tv-home', icon: 'tv'});
  const affiliations = repPartnerAction('affiliations', 'REP_PARTNER_ACTION_AFFILIATIONS', {route: '/partner-affiliations', icon: 'users'});
  const reports = repPartnerAction('reports', 'REP_PARTNER_ACTION_REPORTS', {route: '/partner-report', icon: 'report'});

  if (hasBooking && hasMedia) {
    return repPartnerWorkspace('hybrid', [resources, bookings, ...(canCreateOpenMatch ? [openMatch] : []), reviewVideos, highlights, managePlaylists, ...(safeChannels.length ? [replayerTv] : [])]);
  }
  if (hasMedia) {
    return repPartnerWorkspace('media', [planActivity, reviewVideos, highlights, managePlaylists, ...(safeChannels.length ? [replayerTv] : [])]);
  }
  if (hasBooking) {
    return repPartnerWorkspace('booking', [resources, bookings, ...(canCreateOpenMatch ? [openMatch] : []), affiliations, reports]);
  }
  return repPartnerWorkspace('general', [resources, reviewVideos, reports]);
};

const containsAny = (value, aliases) => aliases.some(alias => value.includes(alias));

export const getRepContentSearchQuery = query => normalize(query)
  .replace(/[^a-z0-9\s]/g, ' ')
  .split(/\s+/)
  .filter(term => term.length > 1 && !CONTENT_SEARCH_STOP_WORDS.has(term))
  .join(' ')
  .trim();

const getSport = normalized => Object.keys(SPORT_ALIASES)
  .find(sport => SPORT_ALIASES[sport].some(alias => new RegExp(`\\b${alias}\\b`).test(normalized))) || null;

const startOfDay = value => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (value, days) => {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};

const startOfWeek = value => {
  const date = startOfDay(value);
  const weekday = date.getDay() || 7;
  return addDays(date, 1 - weekday);
};

const startOfMonth = value => {
  const date = startOfDay(value);
  date.setDate(1);
  return date;
};

const getTransactionRange = (normalized, now) => {
  const today = startOfDay(now);
  const mentionsToday = containsAny(normalized, ['oggi', 'today', 'aujourd hui', 'heute']);
  const mentionsYesterday = containsAny(normalized, ['ieri', 'yesterday', 'hier', 'gestern']);
  if (mentionsYesterday && mentionsToday) {
    return {start: addDays(today, -1), end: addDays(today, 1), labelKey: 'REP_RANGE_YESTERDAY_TO_TODAY'};
  }
  if (mentionsYesterday) {
    return {start: addDays(today, -1), end: today, labelKey: 'REP_RANGE_YESTERDAY'};
  }
  if (mentionsToday) {
    return {start: today, end: addDays(today, 1), labelKey: 'REP_RANGE_TODAY'};
  }
  if (/(ultima|scorsa) settimana|ultimi 7 giorni|last (week|7 days)|7 derniers jours|semaine derniere|letzte woche|letzten 7 tage/.test(normalized)) {
    return {start: addDays(today, -7), end: addDays(today, 1), labelKey: 'REP_RANGE_LAST_7_DAYS'};
  }
  if (/questa settimana|current week|this week|cette semaine|diese woche/.test(normalized)) {
    return {start: startOfWeek(today), end: addDays(today, 1), labelKey: 'REP_RANGE_CURRENT_WEEK'};
  }
  if (/questo mese|mese corrente|current month|this month|ce mois|mois courant|dieser monat|diesen monat/.test(normalized)) {
    return {start: startOfMonth(today), end: addDays(today, 1), labelKey: 'REP_RANGE_CURRENT_MONTH'};
  }
  if (/mese scorso|ultimo mese|last month|mois dernier|letzten monat|voriger monat/.test(normalized)) {
    const currentMonth = startOfMonth(today);
    const previousMonth = new Date(currentMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    return {start: previousMonth, end: currentMonth, labelKey: 'REP_RANGE_PREVIOUS_MONTH'};
  }
  return null;
};

const getRelativeDayOffset = normalized => {
  if (containsAny(normalized, ['dopodomani', 'day after tomorrow', 'apres demain', 'ubermorgen'])) return 2;
  if (containsAny(normalized, ['domani', 'tomorrow', 'demain', 'morgen'])) return 1;
  return 0;
};

const getRequestedTime = normalized => {
  const match = normalized.match(/(?:verso le|circa le|around|about|vers|gegen|ungefahr|alle|ore|at|um)\s+(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?\b/);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const meridiem = match[3];
  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return null;
  const approximate = /(?:verso le|circa le|around|about|vers|gegen|ungefahr)\s+\d/.test(normalized);
  return {
    hour,
    minute,
    value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    toleranceMinutes: approximate ? 60 : 0
  };
};

const getOpenMatchOptions = normalized => {
  const customLevel = normalized.match(/(?:livello|level|niveau|niveau de jeu|spielstarke)\s*(\d+(?:[.,]\d+)?)\s*(?:-|–|a|to|bis|au)\s*(\d+(?:[.,]\d+)?)/);
  const minLevel = customLevel ? Number(customLevel[1].replace(',', '.')) : null;
  const maxLevel = customLevel ? Number(customLevel[2].replace(',', '.')) : null;
  const anyLevel = /(?:qualsiasi|ogni) livello|any level|tout niveau|jedes niveau/.test(normalized);
  const playersRequired = /\b(?:singolo|singles?|simple|einzel)\b/.test(normalized) ? 2 : 4;
  const matchType = /\b(?:competitiva|competitivo|competitive|competitif|wettkampf)\b/.test(normalized) ? 'competitive' : 'friendly';
  let gender = 'any';
  if (/\b(?:mista|misto|mixed|mixte|gemischt)\b/.test(normalized)) gender = 'mixed';
  else if (/\b(?:femminile|female|women|feminin|damen)\b/.test(normalized)) gender = 'female';
  else if (/\b(?:maschile|male|men|masculin|herren)\b/.test(normalized)) gender = 'male';
  return {
    playersRequired,
    matchType,
    gender,
    levelRangeMode: customLevel ? 'custom' : (anyLevel ? 'any' : 'similar'),
    minLevel,
    maxLevel
  };
};

const validVisibilityDays = value => {
  if (value === null || value === undefined || value === '') return null;
  const days = Number(value);
  return Number.isFinite(days) && days >= 0 ? days : null;
};

export const getRepBookingVisibilityDays = partner => {
  if (!partner) return null;
  const membershipDays = validVisibilityDays(partner.visibilityCalendar);
  if (partner.membership && membershipDays !== null) return membershipDays;
  return validVisibilityDays(partner.defaultVisibilityCalendar);
};

export const getRepBookingSearchWindow = (partner, now = new Date()) => {
  const days = getRepBookingVisibilityDays(partner);
  if (days === null) return null;
  const start = startOfDay(now);
  const end = new Date(now);
  end.setDate(end.getDate() + days);
  return {start, end, days};
};

export const isRepBookingDateVisible = (partner, targetDate, now = new Date()) => {
  const window = getRepBookingSearchWindow(partner, now);
  if (!window || !targetDate) return true;
  return new Date(targetDate) >= window.start && new Date(targetDate) < window.end;
};

export const getRepVideoSearchWindow = (now = new Date()) => {
  const start = startOfDay(now);
  start.setDate(start.getDate() - 15);
  const end = startOfDay(now);
  end.setDate(end.getDate() + 2);
  return {start, end, pastDays: 15, futureDays: 1};
};

const getContentDateRange = (normalized, now) => {
  let offset = null;
  if (containsAny(normalized, ['ieri', 'yesterday', 'hier', 'gestern'])) offset = -1;
  else if (containsAny(normalized, ['oggi', 'today', 'aujourd hui', 'heute'])) offset = 0;
  else if (containsAny(normalized, ['dopodomani', 'day after tomorrow', 'apres demain', 'ubermorgen'])) offset = 2;
  else if (containsAny(normalized, ['domani', 'tomorrow', 'demain', 'morgen'])) offset = 1;
  if (offset === null) return null;
  const start = addDays(startOfDay(now), offset);
  return {start, end: addDays(start, 1)};
};

export const parseRepRequest = (query, now = new Date(), language = 'it') => {
  const normalized = normalize(query);
  const concepts = getRepConcepts(query);
  const supportTopic = findRepSupportTopic(query, language);
  const guideIntent = /(?:come|spiega|guida|aiut|cosa devo|come faccio|cos e|che cos e|a cosa serve|how (?:do|can|does|to)|explain|help|what is|comment|aide|qu est ce|wie (?:kann|funktioniert|erstelle|mache)|erklare|hilfe|was ist)/.test(normalized);
  const contextualSection = concepts.includes('analysis') ? 'analysis' : (concepts.includes('highlight') ? 'highlight' : null);
  const myVideoSection = MY_VIDEO_SECTIONS.find(section => section.key === contextualSection)
    || MY_VIDEO_SECTIONS.find(section => section.aliases.some(alias => normalized.includes(alias)))
    || null;
  const sectionNavigationIntent = Boolean(myVideoSection) && /(?:apri|vai|entra|cerca|mostra|fammi vedere|open|show|go to|ouvre|montre|offne|zeige)/.test(normalized) && !guideIntent;
  const transactionIntent = /(transazion|moviment|rimborso|rimbors|transaction|movement|refund|mouvement|remboursement|transaktion|umsatz|erstattung)/.test(normalized);
  const playerReportIntent = /(?:genera|crea|scarica|esporta|generate|create|download|export|generer|creer|telecharger|exporter|erstellen|herunterladen|exportieren).*(?:(?:report|elenco|liste|list).*(?:player|giocator|utenti affiliati|affiliat|spieler|joueur)|(?:player|giocator|utenti affiliati|affiliat|spieler|joueur).*(?:report|elenco|liste|list))/.test(normalized);
  const walletTopupIntent = /(?:ricaric|aggiung|recharg|top up|add).*(?:credito|credit|wallet|euros?|€|guthaben)|(?:aufladen|lade .* auf)/.test(normalized);
  const walletBalanceIntent = /(?:quanto|mostra|saldo|how much|show|balance|solde|combien|guthaben|wie viel).*(?:credito|credit|wallet|solde|guthaben)/.test(normalized);
  const myVideoIntent = /\bmy\s*video\b|\bi miei video\b|\bmes videos\b|\bmeine videos\b/.test(normalized);
  const openMatchesIntent = /partite aperte|open matches|parties ouvertes|offene (spiele|matches)/.test(normalized);
  const createOpenMatchIntent = /(?:crea|creare|organizza|organizzare|create|organize|organise|cree|erstelle|organisiere).*(?:partita aperta|open(?:\s+\w+){0,3}\s+match|partie ouverte|offenes (?:spiel|match))/.test(normalized);
  const myBookingsIntent = /mie(?:\s+\w+){0,3}\s+prenotazioni|my(?:\s+\w+){0,3}\s+bookings|mes(?:\s+\w+){0,3}\s+reservations|meine(?:\s+\w+){0,3}\s+buchungen/.test(normalized);
  const subscriptionStatusIntent = /(?:quando|quanto|mostra|scad|stato|when|show|expire|status|quand|expire|etat|wann|zeige|ablauf|status).*(?:abbonament|subscription|abonnement|abo)|(?:abbonament|subscription|abonnement|abo).*(?:scad|attiv|when|status|expire|etat|ablauf)/.test(normalized);
  const pendingActionsIntent = /(?:inviti?|invitation|einladung|risultat|result|ergebnis).*(?:confermare|conferma|pending|confirm|attesa|confirmer|bestatigen)|(?:cosa|what|que|was).*(?:richiede attenzione|needs attention|a verifier|aufmerksamkeit)/.test(normalized);
  const partnerOverviewIntent = /(?:riepilogo|situazione|overview|summary|resume|ubersicht).*(?:oggi|today|aujourd hui|heute)|(?:prenotazioni|bookings|reservations|buchungen|occupazione|occupation|auslastung).*(?:oggi|today|aujourd hui|heute)/.test(normalized);
  const videoIntent = /(video|partita|match|replayertv|replayer tv|diretta|live|streaming|partie|spiel)/.test(normalized);
  const contentSearchQuery = getRepContentSearchQuery(query);
  const contentSearchIntent = !myVideoIntent && !sectionNavigationIntent && (
    /\b(?:contro|versus|vs|against|contre|gegen)\b/.test(normalized)
    || (videoIntent && contentSearchQuery.split(/\s+/).filter(Boolean).length > 0)
  );
  const bookingIntent = /(campo|prenot|slot|liber|disponibil|court|field|book|available|terrain|reserver|platz|buchen|verfugbar)/.test(normalized);
  const sport = getSport(normalized);
  const requestedTime = getRequestedTime(normalized);
  const amountMatch = normalized.match(/(?:€|euros?)\s*(\d+(?:[.,]\d{1,2})?)\b|\b(\d+(?:[.,]\d{1,2})?)\s*(?:€|euros?)\b|(?:ricaric\w*|recharg\w*|top up|add|aufladen)(?:\s+\w+){0,4}\s+(\d+(?:[.,]\d{1,2})?)\b/);
  const amount = amountMatch ? amountMatch.slice(1).find(Boolean) : null;
  const paymentMethod = ['satispay', 'paypal', 'apple pay', 'google pay'].find(method => normalized.includes(method))
    || (/(?:carta|credit card)/.test(normalized) ? 'card' : null);
  const targetDate = startOfDay(now);
  const transactionRange = transactionIntent ? getTransactionRange(normalized, now) : null;
  const sectionStopWords = new Set(['apri', 'aprire', 'vai', 'entra', 'cerca', 'mostra', 'mostrami', 'fammi', 'vedere', 'nella', 'nelle', 'sezione', 'cartella', 'my', 'video', 'replayer', 'partner', 'highlight', 'analisi', 'playlist', 'tag']);
  const sectionTarget = sectionNavigationIntent
    ? normalized.split(/\s+/).filter(term => term.length > 2 && !sectionStopWords.has(term)).join(' ').trim() || null
    : null;

  targetDate.setDate(targetDate.getDate() + getRelativeDayOffset(normalized));
  if (requestedTime) targetDate.setHours(requestedTime.hour, requestedTime.minute, 0, 0);

  let intent = 'unknown';
  if (supportTopic) intent = 'support';
  else if (guideIntent) intent = 'guide';
  else if (sectionNavigationIntent) intent = 'my-video-section';
  else if (playerReportIntent) intent = 'player-report';
  else if (transactionIntent) intent = 'transaction-report';
  else if (walletTopupIntent) intent = 'wallet-topup';
  else if (walletBalanceIntent) intent = 'wallet-balance';
  else if (myVideoIntent) intent = 'my-video';
  else if (createOpenMatchIntent) intent = 'create-open-match';
  else if (openMatchesIntent) intent = 'open-matches';
  else if (myBookingsIntent) intent = 'my-bookings';
  else if (subscriptionStatusIntent) intent = 'subscription-status';
  else if (pendingActionsIntent) intent = 'pending-actions';
  else if (partnerOverviewIntent) intent = 'partner-overview';
  else if (bookingIntent) intent = 'booking';
  else if (contentSearchIntent) intent = 'search-content';
  else if (videoIntent) intent = 'video';

  return {
    intent,
    supportTopic: supportTopic ? supportTopic.key : null,
    myVideoSection: myVideoSection ? myVideoSection.key : null,
    sectionTarget,
    amount: amount ? Number(amount.replace(',', '.')) : null,
    paymentMethod,
    sport,
    targetDate,
    requestedTime: requestedTime ? requestedTime.value : null,
    timeToleranceMinutes: requestedTime ? requestedTime.toleranceMinutes : null,
    transactionRange,
    contentSearchQuery,
    contentDateRange: contentSearchIntent ? getContentDateRange(normalized, now) : null,
    openMatchOptions: createOpenMatchIntent ? getOpenMatchOptions(normalized) : null,
    asksRefundStatus: /(rimborso|rimbors|refund|remboursement|erstattung)/.test(normalized),
    terms: normalized.split(/\s+/).filter(term => term.length > 2),
    concepts,
    language
  };
};

const partnerSearchText = partner => normalize([
  partner.businessName,
  partner.name,
  partner.city,
  partner.address,
  partner.sports,
  ...(partner.rtvChannels || []).map(channel => channel.name || ''),
  ...(partner.categories || []).map(category => category.name || category),
  ...(partner.fields || []).map(field => `${field.name || ''} ${field.sport || ''}`)
].filter(Boolean).join(' '));

const readCoordinate = (source, keys) => {
  if (!source) return null;
  for (let index = 0; index < keys.length; index += 1) {
    const value = source[keys[index]];
    if (value === null || value === undefined || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

export const getRepPartnerCoordinates = partner => {
  const latitude = readCoordinate(partner, ['latitude', 'lat', 'geoLatitude', 'geo_latitude', 'locationLatitude', 'location_latitude']);
  const longitude = readCoordinate(partner, ['longitude', 'lng', 'lon', 'geoLongitude', 'geo_longitude', 'locationLongitude', 'location_longitude']);
  if (latitude !== null && longitude !== null) return {latitude, longitude};

  const fields = partner && Array.isArray(partner.fields) ? partner.fields : [];
  for (let index = 0; index < fields.length; index += 1) {
    const fieldLatitude = readCoordinate(fields[index], ['latitude', 'lat', 'geoLatitude', 'geo_latitude', 'locationLatitude', 'location_latitude']);
    const fieldLongitude = readCoordinate(fields[index], ['longitude', 'lng', 'lon', 'geoLongitude', 'geo_longitude', 'locationLongitude', 'location_longitude']);
    if (fieldLatitude !== null && fieldLongitude !== null) return {latitude: fieldLatitude, longitude: fieldLongitude};
  }

  return null;
};

export const getRepDistanceKm = (origin, destination) => {
  if (!origin || !destination) return null;
  const toRadians = degrees => degrees * Math.PI / 180;
  const earthRadiusKm = 6371;
  const deltaLatitude = toRadians(destination.latitude - origin.latitude);
  const deltaLongitude = toRadians(destination.longitude - origin.longitude);
  const originLatitude = toRadians(origin.latitude);
  const destinationLatitude = toRadians(destination.latitude);
  const haversine = Math.sin(deltaLatitude / 2) ** 2
    + Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(deltaLongitude / 2) ** 2;
  const distance = earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  return Number.isFinite(distance) ? distance : null;
};

export const formatRepDistance = distanceKm => {
  if (distanceKm === null || distanceKm === undefined) return null;
  if (distanceKm < 10) return `${distanceKm.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(distanceKm)} km`;
};

export const findRepPartners = (partners, request, limit = 5, userPosition = null) => {
  const usefulTerms = request.terms.filter(term => !PARTNER_SEARCH_STOP_WORDS.has(term) && !/^\d/.test(term) && term !== request.sport);

  return (partners || [])
    .filter(partner => {
      const text = partnerSearchText(partner);
      const needsBookableField = ['booking', 'create-open-match'].includes(request.intent);
      const bookableSports = partnerBookableSports(partner);
      const sportMatches = !request.sport || (needsBookableField
        ? bookableSports.includes(request.sport)
        : text.includes(request.sport));
      const nameMatches = usefulTerms.length === 0 || usefulTerms.some(term => text.includes(term));
      const bookable = !needsBookableField || partner.bookableFields === true || bookableSports.length > 0;
      const openMatchEnabled = request.intent !== 'create-open-match' || partner.openMatchesEnabled === true;
      return sportMatches && nameMatches && bookable && openMatchEnabled;
    })
    .map(partner => {
      const coordinates = getRepPartnerCoordinates(partner);
      const distanceKm = getRepDistanceKm(userPosition, coordinates);
      return distanceKm === null ? partner : {...partner, repDistanceKm: distanceKm};
    })
    .sort((first, second) => {
      const firstDistance = first.repDistanceKm;
      const secondDistance = second.repDistanceKm;
      if (firstDistance !== undefined && secondDistance !== undefined) return firstDistance - secondDistance;
      if (firstDistance !== undefined) return -1;
      if (secondDistance !== undefined) return 1;
      return String(first.businessName || first.name || '').localeCompare(String(second.businessName || second.name || ''));
    })
    .slice(0, limit);
};

const fieldSport = field => normalize(field && field.sport && (field.sport.name || field.sport));

export const findAvailableRepSlots = (slots, fields, request, now = new Date()) => {
  if (!request || !request.targetDate) return [];

  return (slots || []).filter(slot => {
    const start = new Date(slot.startDatetime);
    const field = (fields || []).find(item => item.id === slot.fieldId);
    const requestedDate = request.targetDate;
    const sameDay = start.getFullYear() === requestedDate.getFullYear()
      && start.getMonth() === requestedDate.getMonth()
      && start.getDate() === requestedDate.getDate();
    const slotMinutes = start.getHours() * 60 + start.getMinutes();
    const requestedParts = request.requestedTime ? request.requestedTime.split(':').map(Number) : null;
    const requestedMinutes = requestedParts ? requestedParts[0] * 60 + requestedParts[1] : null;
    const sameTime = requestedMinutes === null
      || Math.abs(slotMinutes - requestedMinutes) <= Number(request.timeToleranceMinutes || 0);
    const sameSport = !request.sport || !field || fieldSport(field).includes(request.sport);

    return slot.state === 'available'
      && !slot.bookingId
      && field
      && field.bookable === true
      && start > now
      && sameDay
      && sameTime
      && sameSport;
  }).sort((first, second) => new Date(first.startDatetime) - new Date(second.startDatetime));
};
